import { useOrganicStag } from 'modules/common/hooks/useOrganicStag';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getGoogleAuthUrl } from '../helpers/googleAuthUrl';
import {
  googleAuthRedirectStorage,
  pendingGoogleAuthStorage,
} from '../helpers/pendingGoogleAuth';

export const useGoogleAuth = (onSendGAEvent: () => void, redirect?: string) => {
  const { syncStag } = useOrganicStag();

  const [pending, setPending] = useState(false);

  // Pending state only lands on the next render, so a double click would
  // otherwise send the event and leave the page twice.
  const isStartedRef = useRef(false);

  // Coming back from the provider can restore the page from bfcache with the
  // state intact, which would leave the button latched off.
  useEffect(() => {
    const reset = () => {
      isStartedRef.current = false;
      setPending(false);
    };

    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const startGoogleAuth = useCallback(async () => {
    if (isStartedRef.current) return;

    isStartedRef.current = true;
    setPending(true);
    onSendGAEvent();

    // The account is created server-side during the provider callback, so the
    // organic stag has to reach the platform before we leave the page.
    await syncStag().catch(() => null);

    pendingGoogleAuthStorage.replace(true);

    // A modal prop cannot survive the round trip to the provider.
    if (redirect) {
      googleAuthRedirectStorage.replace(redirect);
    } else {
      googleAuthRedirectStorage.remove();
    }

    window.location.assign(getGoogleAuthUrl());
  }, [onSendGAEvent, redirect, syncStag]);

  return { startGoogleAuth, pending };
};
